import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Events from "@/pages/Events";
import Catalog from "@/pages/Catalog";
import Customers from "@/pages/Customers";
import Orders from "@/pages/Orders";
import Payments from "@/pages/Payments";
import ShoppingList from "@/pages/ShoppingList";
import Allocation from "@/pages/Allocation";
import Bought from "@/pages/Bought";
import Substitution from "@/pages/Substitution";
import CostProfit from "@/pages/CostProfit";
import Boxes from "@/pages/Boxes";
import Team from "@/pages/Team";
import Activity from "@/pages/Activity";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-400">Memuat...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" ? children : <Navigate to="/events" replace />;
}

function AuthCallback() {
  const { exchangeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    exchangeGoogleSession(sid).then((ok) => {
      window.history.replaceState(null, "", location.pathname);
      navigate(ok ? "/events" : "/login", { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="min-h-screen grid place-items-center text-slate-400" data-testid="auth-callback">Memproses login Google...</div>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/events" element={<Events />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/products" element={<Navigate to="/catalog" replace />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/shopping-list" element={<ShoppingList />} />
        <Route path="/bought" element={<Bought />} />
        <Route path="/allocation" element={<Allocation />} />
        <Route path="/substitution" element={<Substitution />} />
        <Route path="/cost-profit" element={<CostProfit />} />
        <Route path="/boxes" element={<Boxes />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/team" element={<AdminOnly><Team /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/events" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
